import React from 'react';

import {Heading, IconButton} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import type {IconName} from '@fortawesome/fontawesome-common-types';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';
import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';

import {HomeActions} from './HomeActions';
import {HomeBanners} from './HomeBanners';

const getGreeting = () => {
  const hour = systemTimeOffsetStore.getCurrentTime().hour();
  if (hour < 12) {
    return 'Good Morning';
  }
  if (hour < 17) {
    return 'Good Afternoon';
  }
  return 'Good Evening';
};

interface Props {
  /** True when the Get Started rail is dismissed — Home then shows the reopen affordance. */
  getStartedCollapsed: boolean;
  onReopenGetStarted: () => void;
}

export const Home = ({getStartedCollapsed, onReopenGetStarted}: Props) => {
  const isMasquerading = masqueradeStore.isMasquerading();
  const userId = masqueradeStore.getUserIdByMasqueradeState() ?? '';
  // While masquerading, read the display name from teacher_v1 (admin-readable) instead of users_v2,
  const greetingQuery = (isMasquerading
    ? resource('teachers_v1').mandarkEndpoint(['teachers_v1', userId])
    : getBaseUserQuery(userId)) as unknown as QueryBuilder;
  const {isReady} = useAreLatestQueriesReady([() => greetingQuery]);
  const greetingResource = isReady
    ? (greetingQuery.getResource() as {getFirstName?: () => string} | null)
    : null;
  const firstName = greetingResource?.getFirstName?.() ?? '';

  return (
    // pb-x20 (160px) gives scroll room below the content so "Classwork" can sit toward the middle (DQA).
    <div className='flex flex-col gap-x3 pb-x20'>
      <div className='mb-x3 flex items-center justify-between gap-x2'>
        <Heading as='h1' className='m-x0 text-[32px]! leading-[1.5]! font-black!' color='primary'>
          {getGreeting()}
          {firstName ? `, ${firstName}` : ''}!
        </Heading>
        {getStartedCollapsed ? (
          <IconButton
            color='secondary'
            icon={['fak', 'regular-square-list-circle-question' as IconName]}
            label='Show Helpful Links'
            onClick={onReopenGetStarted}
            tooltip='Show Helpful Links'
            variant='text'
          />
        ) : null}
      </div>
      <HomeBanners />
      <HomeActions />
    </div>
  );
};
